<template>
    <ul
        class="vf-dropdown__list vert"
        ref="list"
    >
        <li
            class="vf-dropdown__list-item"
            v-for="(item, index) in filteredItems"
            :key="item.key"
            :class="item.class"
            ref="items"
        >
            <slot :name="item.key">
                <component
                    :id="index"
                    :is="item.component || dropdownButton"
                    v-bind="item.componentProps || {}"
                    class="vf-dropdown__list-item-button vf-dropdown-list__accent-hover-button vf-no-outline"
                    :class="item.buttonClass"
                    :disabled="item.disabled"
                    :aria-selected="selection === item.key ? 'true' : 'false'"
                    :data-test="locatorsDropdown.listItem(item.key)"
                    @click="$emit('list-item-clicked', item)"
                >
                    <template
                        v-if="item.icon"
                        #icon="iconProps"
                    >
                        <component
                            :is="item.icon"
                            :class="[ item.iconClass, iconProps.class ]"
                            :circle="item.circle"
                            v-bind="iconProps"
                        />
                    </template>
                    {{ item.value }}
                </component>
            </slot>
        </li>
    </ul>
</template>

<script>
import dropdownButton from '../buttons/dropdown-button.vue';
import { UP, DOWN } from 'commons/constants/keycodes.js';
import { locatorsDropdown } from '../e2e/locators.js';

export default {
    props: {
        items: {
            type: Array,
            required: true,
        },
        selection: {
            default: null,
        },
    },

    data: () => ({
        locatorsDropdown,
    }),

    beforeCreate() {
        this.dropdownButton = dropdownButton;
    },

    mounted() {
        this.$nextTick(() => {
            const firstItem = this.$refs.items[0].children[0];
            if (firstItem) {
                firstItem.focus();
            }
            this.$refs.list.addEventListener('keydown', this.onKeydown);
        });
    },

    destroyed() {
        if (this.$refs.list) {
            this.$refs.list.removeEventListener('keydown', this.onKeydown);
        }
    },

    computed: {
        filteredItems() {
            return this.items
                .filter((item) => this.test(item))
                .map((item) => ({
                    ...item,
                    iconClass: this.getIconClass(item),
                    circle: this.getIconCircle(item.iconCircle),
                }));
        },
    },

    methods: {
        test(item) {
            return item.visible === undefined || item.visible;
        },

        getIconClass({ iconSize, iconClass }) {
            const iconSizeClass = iconSize ? `vf-icon-${iconSize}` : 'vf-icon-small';

            return {
                [iconSizeClass]: true,
                [iconClass]: iconClass,
            };
        },

        getIconCircle(circle) {
            return circle ? circle : '';
        },

        getFocusedElementId() {
            return parseInt(document.activeElement.id, 10);
        },

        focusOnNext() {
            const prevElemPosition = this.getFocusedElementId();
            if (prevElemPosition < this.filteredItems.length - 1) {
                const nextFocusedElement = this.$refs.items[prevElemPosition + 1].children[0];
                nextFocusedElement.focus();
            }
            else {
                this.$refs.items[0].children[0].focus();
            }
        },
        focusOnPrevious() {
            const prevElemPosition = this.getFocusedElementId();
            if (prevElemPosition > 0) {
                const nextFocusedElement = this.$refs.items[prevElemPosition - 1].children[0];
                nextFocusedElement.focus();
            }
            else {
                this.$refs.items[this.filteredItems.length - 1].children[0].focus();
            }
        },

        onKeydown(ev) {
            if (ev.keyCode === UP) {
                ev.preventDefault();
                this.focusOnPrevious();
            }
            else if (ev.keyCode === DOWN) {
                ev.preventDefault();
                this.focusOnNext();
            }
        },
    },
};
</script>

<style lang="scss">
@import '../../../css/variables/module';

.vf-dropdown__list-item > * {
    width: 100%;
    line-height: 1;
    white-space: nowrap;
}

.vf-dropdown__list {
    position: relative;
    z-index: 2;
    align-items: stretch;
    padding: 7px;

    @include background($default-palette);
}

.vf-dropdown__list-item-button {
    display: flex;
    align-items: center;
    border-radius: 3px;

    .vf-icon {
        vertical-align: text-top;

        @include light-text($default-palette, fill);
        @include light-text($default-palette, stroke);
    }
}
</style>
